import React, { useState, useEffect } from 'react'
import { Classes, Dialog } from '@blueprintjs/core'
import { Icon, Flex, Toaster } from '@ui'
import css from './spaceType.module.scss'
import { Button } from '@runeffective/evo-design-system'
import { SelectWrapper } from 'components/AdminFloorPlan/components/SelectWrapper'
import api, { FloorMapSpaces, INeighborhood } from '@api'
import { useFloor } from '@providers/mapProvider'
import { get, groupBy } from 'lodash'
import Select2 from '@ui/selects/Select'
import { handleError } from '@ui/canvas/utils'

export interface NeighborhoodDialogProps {
  setisNeighborhoodOpen: (para: boolean) => void
  isNeighborhoodOpen: boolean
  spaces: FloorMapSpaces
  selectedSpaces: string[]
}

export default function NeighborhoodDialog({
  setisNeighborhoodOpen,
  isNeighborhoodOpen,
  spaces,
  selectedSpaces,
}: NeighborhoodDialogProps) {
  const [btnDisabled, setBtnDisabled] = useState(true)
  const [selectedNbh, setSelectedNbh] = useState(null as INeighborhood | null)
  const [listNeighborhood, setListNeighborhood] = useState<INeighborhood[]>([])
  const floor = useFloor()

  function resetState() {
    setBtnDisabled(true)
    setSelectedNbh(null)
  }

  useEffect(() => {
    setNeighborhoodList()
  }, [])

  const formatData = (spaces: FloorMapSpaces, selectedItems: string[]) => {
    const { byId } = spaces
    const items = selectedItems.reduce(
      (result: any, id: string) => (byId[id] ? [...result, byId[id]] : result),
      []
    )
    return items
  }

  function setNeighborhoodList() {
    api.neighborhood.neighborhoodList().then((nhbLists) => {
      setListNeighborhood(nhbLists.filter((nbh) => nbh.floor.id === floor.floorId))
    })
  }

  function onClickSubmit() {
    api.neighborhood
      .addSpaces(selectedNbh?.id as string, selectedSpaces)
      .then(() => {
        Toaster.success({
          message: `${selectedSpaces.length} spaces were associated to ${selectedNbh?.name} neighborhood`,
        })
        setisNeighborhoodOpen(false)
        floor.reloadMap?.()
      })
      .catch(handleError)
    resetState()
  }

  function onClickUnallocate() {
    const formatedData = formatData(spaces, selectedSpaces)

    const filterSpacespaces = formatedData.filter((space: FloorMapSpaces) =>
      get(space, 'spaceAssignment.neighborhood.id')
    )
    const groupedSpaces = groupBy(filterSpacespaces, (space) =>
      get(space, 'spaceAssignment.neighborhood.id')
    )

    Object.keys(groupedSpaces).map(async (key) => {
      let nbh_id = key
      let groupedSpace = groupedSpaces[key]
      const spaceIds = groupedSpace.map((space) => space.id)
      await api.neighborhood
        .removeSpaces(nbh_id, spaceIds)
        .then((response) => {
          let updatedNames = `${spaceIds.length} spaces`

          try {
            if (spaceIds.length < 2) {
              const space = spaceIds[0]
              if (space) {
                updatedNames = space.name || space.externalId
              }
            }
          } catch (e) {}
          Toaster.success({
            message: `${updatedNames} were removed from ${response.name} neighborhood`,
          })
        })
        .catch(handleError)
    })
    floor.reloadMap?.()
    setisNeighborhoodOpen(false)
    resetState()
  }

  return (
    <>
      <Dialog
        style={{ paddingBottom: '0px' }}
        isOpen={isNeighborhoodOpen}
        className={css.dialogWidth}
        transitionDuration={200}
        isCloseButtonShown={true}
        canOutsideClickClose={false}
        onClose={() => setisNeighborhoodOpen(false)}>
        <div className={css.empList}>
          <Button
            style={{ position: 'absolute', top: '5px', right: '10px' }}
            aria-label="single icon button"
            onClick={() => {
              setisNeighborhoodOpen(false)
              resetState()
            }}
            size="default"
            layout="borderless">
            <Icon name="close" />
          </Button>

          <div>
            <div className={css.dialogHeader}> Neighborhood </div>
            <div className={css.labelText}>
              Assign the selected spaces to the appropriate neighborhood
            </div>
          </div>
          <div className={css.emptyDiv}>
            <div className={css.paddingTop}>
              <SelectWrapper title="Select neighborhood">
                <Select2
                  canCreate
                  value={selectedNbh}
                  buttonProps={{ style: { width: 270, borderColor: '#848995' } }}
                  onChange={(nbh) => {
                    setSelectedNbh(nbh)
                    setBtnDisabled(false)
                  }}
                  data={listNeighborhood}
                  onCreate={(nbhName: string) =>
                    api.neighborhood
                      .create({ floorId: floor.floorId as string, name: nbhName })
                      .then((neighborhood) => {
                        Toaster.success({
                          message: 'Neighborhood Created successfully',
                        })
                        setSelectedNbh(neighborhood) as unknown as Promise<INeighborhood>
                        setNeighborhoodList()
                        setBtnDisabled(false)
                      })
                      .catch(handleError)
                  }
                />
              </SelectWrapper>
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={css.emptyDiv}>
              <div className={css.row}>
                <div className={css.leftPanel}>
                  <Button
                    layout="danger"
                    onClick={() => {
                      onClickUnallocate()
                    }}
                    size="default">
                    Unallocate
                  </Button>
                </div>
                <div className={css.rightPanel}>
                  <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Flex style={{ gap: '8px' }} direction="row" justify="space-between">
                      <Button
                        layout="borderless"
                        onClick={() => {
                          setisNeighborhoodOpen(false)
                          resetState()
                        }}
                        size="default">
                        Cancel
                      </Button>
                      <Button
                        disabled={btnDisabled}
                        layout="primary"
                        onClick={() => onClickSubmit()}
                        size="default">
                        Update
                      </Button>
                    </Flex>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  )
}
